/**
 * Timestep Detail Page
 *
 * Displays detailed information for a specific timestep including logs,
 * form status, mapped input values, and navigation steps.
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Container, Stack, Text, Title } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { fetchTimestepDetail } from "../api";
import { TimestepHeader } from "./components/TimestepHeader";
import { FormsStatus } from "./components/FormsStatus";
import { LogsDisplay } from "./components/LogsDisplay";
import { Screenshots } from "./components/Screenshots";
import type { TimestepDetailData } from "../types";
import { AppResources } from "@/constants";

export const TimestepDetail: React.FC = () => {
  const { returnId, timestepId } = useParams<{
    returnId: string;
    timestepId: string;
  }>();
  const [data, setData] = useState<TimestepDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!returnId || !timestepId) {
        setError("Return ID and Timestep ID are required");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const detailData = await fetchTimestepDetail(returnId, timestepId);
        setData(detailData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [returnId, timestepId]);

  if (loading) {
    return (
      <>
        <style>
          {`
            @keyframes fadeInOut {
              0%, 100% {
                opacity: 0.3;
              }
              50% {
                opacity: 1;
              }
            }

            @keyframes gentleFloat {
              0%, 100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-8px);
              }
            }

            .wizzy-loading {
              animation: fadeInOut 2s ease-in-out infinite, gentleFloat 3s ease-in-out infinite;
            }
          `}
        </style>
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 9999,
          }}
        >
          <Stack align="center" gap="md">
            <img
              src={AppResources.wizzyWithGlases}
              alt="Loading..."
              className="wizzy-loading"
              style={{
                width: "120px",
                height: "120px",
              }}
            />
            <Text c="dimmed" size="sm">
              Loading timestep details...
            </Text>
          </Stack>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Data"
          color="red"
          variant="filled"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="No Data Available"
          color="yellow"
        >
          No timestep data found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <div>
          <Title order={1} mb="xs">
            Timestep Detail
          </Title>
          <Text c="dimmed" size="sm">
            Detailed information for timestep {timestepId}
          </Text>
        </div>

        {/* Timestep Header with Navigation */}
        {returnId && (
          <TimestepHeader
            timestep={data.timestep}
            navigation={data.navigation}
            returnId={returnId}
          />
        )}

        {/* Forms Status */}
        <FormsStatus
          missingForms={data.timestep.missingForms}
          inProgressForms={data.timestep.inProgressForms}
          completedForms={data.timestep.completedForms}
          incompleteForms={data.timestep.incompleteForms}
        />

        {/* Logs */}
        <Screenshots screenshots={data.timestep.screenshots ?? []} />

        <LogsDisplay logs={data.logs} logSourceType={data.logSourceType} />
      </Stack>
    </Container>
  );
};
